// Project data model (brief §8, extended). Each project = one record + its images.
// Image descriptors reference files in assets/images/projects/{slug}/{base}-{width}.jpg
//
// A project's body lives in `story`: a flat ordered array of blocks rendered
// top to bottom by project-template.js — image, images (a run), act (a
// labeled narrative beat), beforeAfter, interlude. One system for every
// project, whether it has AT Residence's full documentation or just a
// hero and a single before/after pair.
const PROJECTS = {
  "at-residence": {
    slug: "at-residence",
    featured: true,
    title: "AT Residence",
    kicker: "Crescent Heights · St. Petersburg, FL",
    subtitle: "Bohemian modern, full residential interior design. Bold, lived-in, beautifully layered.",
    heroImage: { base: "hero", widths: [800, 1600, 2400], sizes: "100vw", alt: "AT Residence exterior, Crescent Heights" },
    // Cards (/work index, the related-work gallery) show this instead of
    // heroImage — the exterior stays on the project page itself, but the
    // rooms are the product, so cards get the strongest styled interior
    // instead.
    cardImage: { base: "living-after", widths: [800, 1600], sizes: "(min-width: 821px) 380px, 90vw", alt: "AT Residence living room, designed" },

    location: "St. Petersburg, FL",
    neighborhood: "Crescent Heights",
    year: 2024,
    scope: "Full Residential",
    types: ["Residential"],

    styleLine: "Bohemian Modern / Full Residential Interior Design",

    story: [
      // Reordered (pre-Shopify final-polish pass, 2026-08-19, per explicit
      // request): ba1 now leads, directly under the hero/stats block,
      // with "The Space" act following it — was the reverse. ba1 also
      // dropped its `style` override in favor of a dedicated #ba1 rule
      // (main.css) matching the homepage's own #ba-home full-bleed
      // treatment ("format the slider to replicate the one on our home
      // page but without the on image text" — project-page sliders
      // already render their heading in a plain .reveal-head row above
      // the image rather than overlaid on it, so there was no on-image
      // text to remove here; the ask was purely about matching the
      // homepage's bigger, full-bleed sizing feel).
      {
        type: "beforeAfter",
        id: "ba1",
        className: "ba--lead",
        heading: "The Living Room, before we saw it.",
        hint: "Drag to reveal what we saw first &nbsp;&#8596;",
        // widths includes 2400 (added alongside a resolution upgrade,
        // pre-Shopify final-polish pass, 2026-08-19) — user supplied the
        // real high-res originals (Downloads/AT RESIDENCE, CRESCENT
        // HEIGHTS.zip, 4000-5700px range) for this exact before/after pair;
        // now that ba1 is full-bleed (up to ~1920px on a wide screen), the
        // old 1600px ceiling was the largest available source and would
        // read as soft on the biggest screens. Both before/after
        // regenerated at 800/1600/2400 from those originals — same crop,
        // same composition, just sourced from a sharper master this time,
        // matching the site's own hero-image width convention.
        before: { base: "living-before", widths: [800, 1600, 2400], sizes: "100vw", alt: "Living room, empty" },
        after: { base: "living-after", widths: [800, 1600, 2400], sizes: "100vw", alt: "Living room, designed" }
      },
      // Follow-up (later session): "The Space" act's 2 paragraphs were
      // stacked directly on top of each other in one block — per explicit
      // request, split into two separate act beats instead of edited
      // together. Paragraph 1 stays here, unchanged position. Paragraph 2
      // ("Every room was designed to be read...") moved lower — now its
      // own "The Feeling" act positioned right before the interlude quote,
      // so it reads as a short closing/transition beat into that quote
      // instead of trailing directly after paragraph 1.
      {
        type: "act",
        label: "The Space",
        text: "Colorful, free-spirited, and quietly refined, this full-home transformation balances playful energy with modern sophistication. Located in the historic Crescent Heights neighborhood of <strong>St. Petersburg, Florida</strong>, AT Residence blends rich tones, curated art, and unexpected moments of joy."
      },
      { type: "image", image: { base: "gallery-wall", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Living room gallery wall, curated art collection" } },
      { type: "images", images: [
        { base: "breakfast-nook", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Breakfast nook, patterned wallpaper and woven pendant" },
        { base: "range-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Kitchen range detail" }
      ]},
      { type: "image", image: { base: "kitchen-wide", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Kitchen, full view toward living room" } },
      {
        type: "act",
        label: "The Feeling",
        text: "Every room was designed to be read before it is seen, spaces that reveal themselves slowly, layer by layer, the way a home should. A space that radiates personality. Bold, lived-in, and beautifully layered."
      },
      // "From corner to character" (ba2) removed entirely, per explicit
      // request — the same wall/corner is already covered by a slider
      // elsewhere (ba1's own living-room pair), so this was a duplicate.
      // corner-before/after image files left on disk, unreferenced now —
      // not deleted, no reason to.
      { type: "image", image: { base: "kitchen-island", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Kitchen island, full living space" } },
      // Moved to directly after the kitchen-island image (2026-08-21, per
      // explicit report — "was supposed to go under the image that is
      // below it"). Was previously positioned between "The Feeling" act
      // and this same image, i.e. above it instead of below.
      {
        type: "interlude",
        quote: "A testament to the unseen elements that make a space truly resonate.",
        attribution: "Sixth Sense Studio · Meliza Veloz"
      },
      { type: "images", images: [
        { base: "vanity-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Bathroom vanity detail, brass fixtures" },
        { base: "lounge-corner", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Lounge corner, leather chair and bar cart" }
      ]}
    ]
  },

  // Images are a mix of two real candid photos (living-candid, chandelier-
  // detail — no hf_ prefix, straight from the client's own home) and 17
  // AI-generated "after" renders (hf_ prefix = Higgsfield, same convention
  // as the homepage's own hero video, see PROJECT_STATUS.md) built off
  // that home's real furniture/art/collections. Three rooms covered:
  // Living Room (brick fireplace wall, rust velvet chair, built-ins),
  // Sunroom (bay window, boucle swivel chairs, brass étagère), Primary
  // Bedroom (sage linens, walnut vanity). chandelier-detail doesn't match
  // either room's visible fixtures exactly — kept deliberately generic in
  // its alt text rather than assigned to a specific room.
  "ch-residence": {
    slug: "ch-residence",
    title: "CH Residence",
    subtitle: "A collected Tampa home, warmed by everything already loved.",
    heroImage: { base: "hero", widths: [800, 1600, 2400], sizes: "100vw", alt: "CH Residence living room, brick fireplace wall" },
    cardImage: { base: "sunroom-wide", widths: [800, 1600], sizes: "(min-width: 821px) 380px, 90vw", alt: "CH Residence sunroom, boucle swivel chairs" },

    location: "Tampa, Florida",
    date: "Aug 2026",
    styleLine: "Eclectic Collected / Full Residential Interior Design",

    story: [
      { type: "image", image: { base: "living-candid", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Living room, cream sofa and rust velvet chair by the fireplace" } },
      {
        type: "act",
        label: "Brief",
        text: [
          "This Tampa home already carried a lifetime of stories, inherited furniture, well-loved art, decades of memory in nearly every room. The brief wasn't to start over. It was to let all of it live together on purpose, instead of by accident.",
          "Every piece already had a place in the family. Our job was to give it a place in the room."
        ]
      },
      { type: "images", images: [
        { base: "living-angle", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Living room, fireplace and built-in bookshelves" },
        { base: "mantle-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Mantle styling detail, brass mirror and dried florals" }
      ]},
      { type: "image", image: { base: "living-chair", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Rust velvet lounge chair by the fireplace" } },
      { type: "images", images: [
        { base: "thistle-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Coffee table styling detail, dried thistle and leather book" },
        { base: "chandelier-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Chandelier detail, brass arms and glass globes" }
      ]},
      {
        type: "act",
        label: "Concept",
        text: "We built each room around what was already loved, a brass étagère, a well-read library, a mother's china, then layered in new pieces that felt like they'd always belonged beside them. Nothing here was chosen to impress. It was chosen to stay."
      },
      { type: "image", image: { base: "sunroom-wide", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Sunroom, boucle swivel chairs and brass étagère" } },
      { type: "images", images: [
        { base: "sunroom-chairs", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Boucle swivel chairs, terrazzo side table" },
        { base: "sunroom-mirror", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Arched mirror reflecting the sunroom seating" }
      ]},
      { type: "images", images: [
        { base: "etagere-wide", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Brass étagère, styled shelves" },
        { base: "etagere-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Étagère detail, clock and framed photo" }
      ]},
      { type: "images", images: [
        { base: "sofa-olive", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Sofa detail, faux olive tree and textured art" },
        { base: "coffee-table-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Coffee table styling detail, rose quartz and design book" }
      ]},
      {
        type: "act",
        label: "Result",
        text: "A fireplace wall as warm as the conversations it's seen. A sunroom that holds the afternoon light like it was designed for exactly this. A bedroom quiet enough to end the day in. The house doesn't read as decorated. It reads as lived in, on purpose."
      },
      { type: "image", image: { base: "bedroom-wide", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Primary bedroom, sage linens and walnut furniture" } },
      { type: "images", images: [
        { base: "headboard-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Headboard detail, linen pillows and abstract art" },
        { base: "nightstand-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Nightstand styling detail, framed photo and carved bowl" }
      ]},
      { type: "images", images: [
        { base: "bedroom-angle", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Primary bedroom, second angle with fringed accent chair" },
        { base: "vanity-detail", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Walnut vanity detail, brass mirror and candlesticks" }
      ]},
      {
        type: "interlude",
        quote: "A home doesn't need matching. It needs meaning, one piece at a time, until every room remembers who lives there.",
        attribution: "Sixth Sense Studio · Meliza Veloz"
      }
    ]
  },

  "ab-residence": {
    slug: "ab-residence",
    featured: true,
    title: "AB Residence",
    subtitle: "A waterfront high-rise, designed to let the view lead.",
    heroImage: { base: "hero", widths: [800, 1600, 2000], sizes: "100vw", alt: "AB Residence living and dining room, waterfront view" },

    location: "St. Petersburg, Florida",
    date: "Feb 2024",
    styleLine: "Monochrome Coastal / High-Rise Residential Interior Design",

    story: [
      { type: "image", image: { base: "living-room-wide", widths: [800, 1600, 2000], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Living room, waterfront view" } },
      {
        type: "act",
        label: "Brief",
        text: "A high-rise residence on St. Petersburg's waterfront arrived with the hard part already solved: the view. The brief wasn't to compete with it. It was to build rooms quiet enough to let the water stay the loudest thing in them."
      },
      { type: "image", image: { base: "dining-wide", widths: [800, 1600, 2000], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Dining room under a glass-bubble chandelier" } },
      {
        type: "act",
        label: "Concept",
        text: "We held the palette to white, black, and warm wood, so nothing inside argues with what's outside the glass. A handful of considered objects, a telescope, blackened vases, brass candle stands, do the work most homes ask color to do."
      },
      { type: "images", images: [
        { base: "kitchen", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Kitchen, pendant lighting over the island" },
        { base: "kitchen-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Kitchen, second angle" },
        { base: "telescope", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Brass telescope, styling detail" }
      ]},
      { type: "images", images: [
        { base: "vase", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Blackened vases, console styling detail" },
        { base: "candles", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Candle stands, dining table detail" },
        { base: "couch-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Living room styling detail" }
      ]},
      {
        type: "act",
        label: "Result",
        text: "A grand piano gets a room of its own instead of a corner. Two bedrooms read as distinct without competing for attention. What's left is a residence that never has to raise its voice. The view already did."
      },
      { type: "images", images: [
        { base: "music-room", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Music room with grand piano and city view" },
        { base: "bedroom-primary", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Primary bedroom" },
        { base: "bedroom-primary-detail", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Primary bedroom, nightstand detail" }
      ]},
      { type: "images", images: [
        { base: "bedroom-guest", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Guest bedroom, geometric rug" },
        { base: "bedroom-guest-view", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Guest bedroom, city view" },
        { base: "couch", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Living room, styling detail" }
      ]},
      { type: "images", images: [
        { base: "bathroom", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Powder room vanity" },
        { base: "balcony", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Balcony, marina view" }
      ]},
      {
        type: "interlude",
        quote: "The best room in the house is the one you didn't have to design. It was already outside the window.",
        attribution: "Sixth Sense Studio · Meliza Veloz"
      }
    ]
  },

  "jw-residence": {
    slug: "jw-residence",
    title: "JW Residence",
    subtitle: "Rebuilt from the studs, restored past what the water took.",
    heroImage: { base: "hero", widths: [800, 1600, 2400], sizes: "100vw", alt: "JW Residence living room, Clearwater" },

    location: "Clearwater, Florida",
    date: "May 2026",
    styleLine: "Coastal Renewal / Full Reconstruction Interior Design",

    story: [
      { type: "image", image: { base: "kitchen-wide", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Kitchen, full reconstruction" } },
      {
        type: "act",
        label: "Brief",
        text: "This Clearwater home took on water before it ever took on character. The rebuild started from the studs, everything the flood touched came back new. A full reconstruction is also a rare kind of permission, the chance to build a better home than the one that flooded."
      },
      { type: "images", images: [
        { base: "dining", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Dining nook" },
        { base: "bedroom", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Primary bedroom" }
      ]},
      {
        type: "act",
        label: "Result",
        text: "Warm wood floors where there used to be water damage. A coastal palette that reads like intention, not insurance paperwork. What was lost is easy to forget in a home that finally feels like it was always meant to look this way."
      },
      { type: "image", image: { base: "twin-bedroom", widths: [800, 1600, 2400], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Twin bedroom, coastal palette" } },
      {
        type: "interlude",
        quote: "A flood takes a house down to what's essential. Everything that comes back gets a second chance to be right.",
        attribution: "Sixth Sense Studio · Meliza Veloz"
      }
    ]
  },

  "mv-residence": {
    slug: "mv-residence",
    featured: true,
    title: "MV Residence",
    subtitle: "A quiet facade, a house full of surprises inside.",
    heroImage: { base: "hero", widths: [800, 1600, 2048], sizes: "100vw", alt: "MV Residence exterior, North B, Tampa" },
    // See at-residence's cardImage comment — same reasoning.
    cardImage: { base: "living-room", widths: [800, 1200], sizes: "(min-width: 821px) 380px, 90vw", alt: "MV Residence living room, curved sofa" },

    location: "North B, Tampa, Florida",
    date: "Oct 2025",
    styleLine: "Eclectic Contemporary / Full Residential Interior Design",

    story: [
      { type: "image", image: { base: "entrance-wide", widths: [800, 1600, 2048], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Entry hall, checkerboard floor and staircase" } },
      {
        type: "act",
        label: "Brief",
        text: "A two-story home in Tampa's North B neighborhood had good bones and no point of view. The work wasn't renovation in the usual sense. It was giving every room its own reason to be remembered, rather than letting the whole house read as one long hallway."
      },
      { type: "images", images: [
        { base: "living-room", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Living room, curved sofa" },
        { base: "kitchen", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Kitchen, waterfall marble island" },
        { base: "dining", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Dining room, brass pendants" }
      ]},
      { type: "images", images: [
        { base: "upstairs-living", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Upstairs living room" },
        { base: "staircase", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Upper landing and staircase" }
      ]},
      {
        type: "act",
        label: "Concept",
        text: "Pattern became the throughline: checkerboard stone in the entry, a hex-tiled bath, a wallpaper too bold to whisper. Where the palette went quiet, in the primary suite, in the living room, it went quiet in slab stone and unbroken white, so the loud rooms would have somewhere calm to return to."
      },
      { type: "images", images: [
        { base: "guest-bath", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Guest bath, graphic wallpaper" },
        { base: "bathroom-hex", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Bathroom, black hexagon tile" },
        { base: "master-bath", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Primary bath, slab stone" }
      ]},
      { type: "images", images: [
        { base: "office", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Study, dark walls and brass chandelier" },
        { base: "study", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Second study, gallery wall" }
      ]},
      {
        type: "act",
        label: "Result",
        text: "Walk the house end to end and no two rooms make the same argument, but none of them argue with each other either. It's the rare renovation where every room is someone's favorite."
      },
      { type: "images", images: [
        { base: "master-bed", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Primary bedroom" },
        { base: "bedroom-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Guest bedroom" },
        { base: "guest-bed", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Guest bedroom, second angle" }
      ]},
      { type: "images", images: [
        { base: "kitchen-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Kitchen, second angle" },
        { base: "living-room-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Living room, second angle" },
        { base: "master-shower", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Primary walk-in shower" }
      ]},
      { type: "images", images: [
        { base: "office-nook", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Office nook, checkerboard floor" },
        { base: "powder-bath", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Powder bath" },
        { base: "bathroom-vanity", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Bathroom, wood double vanity" }
      ]},
      { type: "images", images: [
        { base: "backyard", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Backyard" },
        { base: "study-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Second study, alternate angle" },
        { base: "master-bed-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Primary bedroom, second angle" }
      ]},
      {
        type: "interlude",
        quote: "A house doesn't need to whisper to feel calm. It needs rooms that know when to speak and when to let the last one finish.",
        attribution: "Sixth Sense Studio · Meliza Veloz"
      }
    ]
  },

  "gs-residence": {
    slug: "gs-residence",
    featured: true,
    title: "GS Residence",
    subtitle: "Premium treatment for a space that refused to be ordinary.",
    heroImage: { base: "hero", widths: [800, 1600, 2048], sizes: "100vw", alt: "GS Residence primary bedroom, Tampa" },

    location: "Tampa, Florida",
    date: "Nov 2023",
    styleLine: "Elevated Residential / Condominium Interior Design",

    // beforeAfter → image → Brief → image → Concept → images → images → Result → images
    // Reordered (pre-Shopify final-polish pass, 2026-08-19) to lead with the
    // slider, matching the pattern established on AT Residence — was
    // image → Brief → beforeAfter → image → Concept → ... Every photo in
    // the folder lands somewhere in this sequence — nothing orphaned.
    story: [
      {
        type: "beforeAfter",
        id: "ba-gs-residence",
        className: "ba--lead",
        heading: "Before we saw it.",
        hint: "Drag to reveal what we saw first &nbsp;&#8596;",
        before: { base: "living-before", widths: [800, 1600], sizes: "100vw", alt: "Living room, empty" },
        after: { base: "living-after", widths: [800, 1024], sizes: "100vw", alt: "Living room, designed" }
      },
      { type: "image", image: { base: "kitchen-wide", widths: [800, 1600, 2048], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Kitchen, GS Residence condominium" } },
      {
        type: "act",
        label: "Brief",
        text: "Within a collection of Tampa condominiums, one unit was given permission to become something more. The brief was quiet but exacting: take a space defined by its sameness and give it a sense of self."
      },
      { type: "image", image: { base: "bedroom-wide", widths: [800, 1600, 2048], sizes: "(min-width: 1500px) 1500px, 100vw", alt: "Primary bedroom, alternate angle" } },
      {
        type: "act",
        label: "Concept",
        text: "We designed against the grid. Where the building offered repetition, we introduced intention, warmth layered into corners the floor plan never asked us to consider, materials chosen for how they feel at arm's length, not how they photograph from the door. The result reads less like a unit and more like a decision. Every surface earns its place."
      },
      { type: "images", images: [
        { base: "kitchen-2", widths: [800, 1200], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Kitchen, second angle" },
        { base: "living-room-angle2", widths: [800, 1024], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Living room" },
        { base: "entryway", widths: [800, 1024], sizes: "(min-width: 821px) 33vw, 100vw", alt: "Entryway and dining nook" }
      ]},
      { type: "images", images: [
        { base: "bedroom-1", widths: [800, 1024], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Bedroom with citrus accent art" },
        { base: "masterbed-apt1", widths: [800, 1024], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Primary bedroom, blush palette" }
      ]},
      {
        type: "act",
        label: "Result",
        text: "What remains is a residence that no longer remembers it was ever ordinary. Square footage became proportion. A floor plan became a home. This is what premium treatment means when it's done by feel, not by budget alone."
      },
      { type: "images", images: [
        { base: "bdr-apt2", widths: [800, 1200], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Bedroom with leather headboard" },
        { base: "bdr-2", widths: [800, 1024], sizes: "(min-width: 821px) 50vw, 100vw", alt: "Bedroom with graphic art and red bedding" }
      ]}
    ]
  }
};
